import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Icon from '@mdi/react';
import { mdiClose, mdiContentCopy, mdiInformationOutline, mdiSend } from '@mdi/js';
import { observer } from 'mobx-react';
import { Tooltip } from '../base';
import { useAi } from '../../services/ai-adapter/hook';
import type { AiContext, AiConversationId, AiTask } from '../../services/ai-adapter/protocol';
import { AiEntityDescriptor, aiEntitySchemas } from '../../services/ai-adapter/entities-description';
import { buildUtilityFromKey } from '../../services/ai-adapter/utility/utility';
import {
    AttachableEntityGroup,
    AttachableEntityItem,
    AttachedItem,
    stringifyContextForAi,
} from '../../services/ai-adapter/smart-context';
import {
    isPlainDocument,
    loadDocumentAsAiFile,
    resolveDocumentApiSide,
} from '../../services/ai-adapter/load-document-file';
import {
    AI_MAX_DATA_JSON_CHARS,
    isAiFileAttachGroupKey,
} from '../../services/ai-adapter/ai-file-support';
import rawSuggestions from './suggestions.json';
import * as S from './ai-assistant.styles';
import talkToClaimGif from './TalkToClaim.gif';
import { AttachCascadingSelect } from './attach-cascading-select';

type SuggestionId = string;

type UiChatMessage = { id: string; role: 'user' | 'assistant'; text: string };

const copyToClipboard = (text: string): void => {
    if (navigator?.clipboard?.writeText) {
        void navigator.clipboard.writeText(text);
    }
};

const CopyAnswerButton: React.FC<{ text: string }> = ({ text }) => (
    <Tooltip direction="top" variant="dark" title={<span>Copy answer</span>}>
        <S.CopyIconButton
            variant="text"
            type="button"
            aria-label="Copy answer"
            onClick={() => copyToClipboard(text)}>
            <Icon path={mdiContentCopy} size={0.65} />
        </S.CopyIconButton>
    </Tooltip>
);

const buildContextFingerprint = (attachedItems: AttachedItem[], matchedKeys: string[]): string =>
    JSON.stringify({
        attached: attachedItems.map(item => `${item.type}:${item.id}`).sort(),
        matchedKeys: [...matchedKeys].sort(),
    });

const generateLocalId = (): string => {
    const maybeCrypto = typeof crypto !== 'undefined' ? crypto : undefined;
    if (maybeCrypto && typeof (maybeCrypto as any).randomUUID === 'function') {
        return (maybeCrypto as any).randomUUID();
    }
    const base = Math.random().toString(36).slice(2) + Date.now().toString(36);
    return base.slice(0, 20);
};

type SuggestReplySegment = { type: 'text' | 'reply'; text: string };

/** Split suggest-reply answers that wrap copyable replies in <<<REPLY_START>>>…<<<REPLY_END>>>. */
const parseSuggestReplyResponseToSegments = (raw: string): SuggestReplySegment[] => {
    const text = raw ?? '';
    if (!text.trim()) {
        return [];
    }

    const markerRe = /<<<REPLY_START>>>\s*([\s\S]*?)\s*<<<REPLY_END>>>/gi;
    const segments: SuggestReplySegment[] = [];

    let lastIndex = 0;
    let match: RegExpExecArray | null = null;
    while ((match = markerRe.exec(text)) !== null) {
        const start = match.index ?? 0;
        const outside = text.slice(lastIndex, start).trim();
        if (outside) {
            segments.push({ type: 'text', text: outside });
        }

        const reply = (match[1] ?? '').trim();
        if (reply) {
            segments.push({ type: 'reply', text: reply });
        }

        lastIndex = start + match[0].length;
    }

    const tail = text.slice(lastIndex).trim();
    if (tail) {
        segments.push({ type: 'text', text: tail });
    }

    return segments;
};

const hasSuggestReplyMarkers = (text: string): boolean =>
    /<<<REPLY_START>>>/i.test(text) && /<<<REPLY_END>>>/i.test(text);

interface ISuggestionDefinition {
    id: SuggestionId;
    label: string;
    template: string;
    /** Maps to chat request `task`. */
    action: AiTask;
}

type ISuggestionsMap = Record<string, ISuggestionDefinition[]>;

const suggestionsMap: ISuggestionsMap = rawSuggestions as ISuggestionsMap;

export type ResolveContextDataResult = {
    context: unknown;
    matchedKeys?: string[];
};

export interface IAiAssistantProps {
    /** Controls whether the floating assistant panel is visible. */
    open: boolean;
    /** Called when the panel should be closed (e.g. via Close/Cancel). */
    onClose: () => void;
    /**
     * Conversation scope for the extension (`claim:…`, `message:…`, …).
     * Build with `buildAiConversationId`.
     */
    conversationId: AiConversationId;
    /** Either a simple model name (e.g. "SupportClaim") or a full descriptor. */
    entity: string | AiEntityDescriptor;
    subjectLabel?: string;
    /** Key into suggestions.json. Defaults to "default". */
    suggestionsKey?: string;
    /** Arbitrary data snapshot that will be serialized into context.dataJson. */
    contextData?: unknown;
    /**
     * Optional per-send smart context builder. When provided, preferred over static contextData.
     * Return matchedKeys to accumulate keyword categories across the conversation.
     */
    resolveContextData?: (
        question: string,
        attachedItems: AttachedItem[],
        matchedKeys: string[],
    ) => ResolveContextDataResult | unknown;
    /** Optional attachable entity groups for the Attach picker. */
    attachableEntities?: AttachableEntityGroup[];
    /** Key into utility-contexts.json describing app/page context. */
    utilityKey?: string;
}

interface IFloatingPosition {
    x: number;
    y: number;
}

const normalizeResolveResult = (result: ResolveContextDataResult | unknown): ResolveContextDataResult => {
    if (
        result &&
        typeof result === 'object' &&
        'context' in (result as Record<string, unknown>) &&
        (result as ResolveContextDataResult).context !== undefined
    ) {
        return result as ResolveContextDataResult;
    }
    return { context: result };
};

export const AiAssistant: React.FC<IAiAssistantProps> = props => {
    const {
        open,
        onClose,
        conversationId,
        entity,
        subjectLabel,
        suggestionsKey,
        contextData,
        resolveContextData,
        attachableEntities,
        utilityKey,
    } = props;

    // Lifted above the open early-return so attachments / matched keys persist for the page session.
    const [attachedItems, setAttachedItems] = useState<AttachedItem[]>([]);
    const [matchedKeys, setMatchedKeys] = useState<string[]>([]);

    // The same mounted instance can be reused for another subject (claim route change, another
    // messaging thread), so this state is scoped to a conversation and must not leak into the next one.
    // Reset during render rather than in an effect, so the panel never sees the previous scope.
    const lastConversationIdRef = useRef(conversationId);
    if (lastConversationIdRef.current !== conversationId) {
        lastConversationIdRef.current = conversationId;
        if (attachedItems.length) {
            setAttachedItems([]);
        }
        if (matchedKeys.length) {
            setMatchedKeys([]);
        }
    }

    if (!open) {
        return null;
    }

    const effectiveSubject = subjectLabel || 'item';
    const effectiveSuggestionsKey = suggestionsKey || 'default';

    return (
        <FloatingAiPanel
            onClose={onClose}
            conversationId={conversationId}
            entity={entity}
            contextData={contextData}
            resolveContextData={resolveContextData}
            attachableEntities={attachableEntities}
            attachedItems={attachedItems}
            onAttachedItemsChange={setAttachedItems}
            matchedKeys={matchedKeys}
            onMatchedKeysChange={setMatchedKeys}
            subjectLabel={effectiveSubject}
            suggestionsKey={effectiveSuggestionsKey}
            utilityKey={utilityKey}
        />
    );
};

interface IFloatingAiPanelProps {
    conversationId: AiConversationId;
    entity: string | AiEntityDescriptor;
    contextData?: unknown;
    resolveContextData?: IAiAssistantProps['resolveContextData'];
    attachableEntities?: AttachableEntityGroup[];
    attachedItems: AttachedItem[];
    onAttachedItemsChange: (items: AttachedItem[]) => void;
    matchedKeys: string[];
    onMatchedKeysChange: (keys: string[]) => void;
    onClose: () => void;
    subjectLabel?: string;
    suggestionsKey: string;
    utilityKey?: string;
}

const FloatingAiPanel: React.FC<IFloatingAiPanelProps> = observer(
    ({
        conversationId,
        entity,
        contextData,
        resolveContextData,
        attachableEntities,
        attachedItems,
        onAttachedItemsChange,
        matchedKeys,
        onMatchedKeysChange,
        onClose,
        subjectLabel,
        suggestionsKey,
        utilityKey,
    }) => {
        const entityDescriptor: AiEntityDescriptor =
            typeof entity === 'string' ? { name: entity, ...(aiEntitySchemas[entity] || {}) } : entity;

        const { askAi, loadHistory, historyLoading, chatLoading, error } = useAi();
        const [messages, setMessages] = useState<UiChatMessage[]>([]);
        const [historyReady, setHistoryReady] = useState(false);
        const [question, setQuestion] = useState('');
        const [internalError, setInternalError] = useState<string | null>(null);
        const [showHowToGif, setShowHowToGif] = useState(false);
        const [position, setPosition] = useState<IFloatingPosition>(() => {
            const defaultX = 0;
            const defaultY = 140;
            if (typeof window === 'undefined') {
                return { x: defaultX, y: defaultY };
            }
            const x = Math.max(0, window.innerWidth - 650);
            const y = Math.max(0, defaultY);
            return { x, y };
        });
        const [dragging, setDragging] = useState(false);
        const [dragOffset, setDragOffset] = useState<{ x: number; y: number } | null>(null);
        const [action, setAction] = useState<AiTask>('explain');
        const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
        const messagesContainerRef = React.useRef<HTMLDivElement | null>(null);
        const messagesEndRef = React.useRef<HTMLDivElement | null>(null);
        const lastMessageRef = React.useRef<HTMLDivElement | null>(null);
        const pendingScrollRef = React.useRef<'bottom' | 'top-of-last' | null>(null);
        const lastSentContextFingerprintRef = React.useRef<string | null>(null);

        const presets = useMemo(() => suggestionsMap[suggestionsKey] || suggestionsMap.default || [], [suggestionsKey]);

        const attachedIdsByType = useMemo(() => {
            const map: Record<string, string[]> = {};
            for (const item of attachedItems) {
                if (!map[item.type]) {
                    map[item.type] = [];
                }
                map[item.type].push(item.id);
            }
            return map;
        }, [attachedItems]);

        const loadHistoryRef = React.useRef(loadHistory);
        loadHistoryRef.current = loadHistory;

        useEffect(() => {
            let cancelled = false;
            setHistoryReady(false);
            setMessages([]);
            setInternalError(null);
            lastSentContextFingerprintRef.current = null;

            const run = async () => {
                try {
                    const conversation = await loadHistoryRef.current(conversationId);
                    if (cancelled) {
                        return;
                    }
                    setMessages(
                        (conversation.messages || []).map(message => ({
                            id: message.id,
                            role: message.role,
                            text: message.content,
                        })),
                    );
                    // Open panel → show the end of the thread.
                    pendingScrollRef.current = 'bottom';
                    // If the extension already has turns, follow-ups can omit heavy context.
                    if ((conversation.messages || []).length > 0) {
                        lastSentContextFingerprintRef.current = buildContextFingerprint(
                            attachedItems,
                            matchedKeys,
                        );
                    }
                } catch (e: unknown) {
                    if (cancelled) {
                        return;
                    }
                    setInternalError(
                        typeof e === 'string'
                            ? e
                            : e instanceof Error
                              ? e.message
                              : 'Failed to load chat history',
                    );
                } finally {
                    if (!cancelled) {
                        setHistoryReady(true);
                    }
                }
            };

            void run();
            return () => {
                cancelled = true;
            };
            // Only re-fetch when the conversation scope changes — never on store/loading churn.
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [conversationId]);

        const handleMouseDownHeader = (event: React.MouseEvent<HTMLDivElement>) => {
            event.preventDefault();
            setDragging(true);
            setDragOffset({
                x: event.clientX - position.x,
                y: event.clientY - position.y,
            });
        };

        useEffect(() => {
            if (!dragging || !dragOffset) {
                return;
            }

            const handleMouseMove = (event: MouseEvent) => {
                event.preventDefault();
                const nextX = event.clientX - dragOffset.x;
                const nextY = event.clientY - dragOffset.y;

                setPosition({
                    x: nextX,
                    y: nextY < 0 ? 0 : nextY,
                });
            };

            const handleMouseUp = () => {
                setDragging(false);
                setDragOffset(null);
            };

            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);

            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }, [dragging, dragOffset]);

        const buildContextPayload = async (
            userMessage: string,
            sendFullContext: boolean,
        ): Promise<AiContext> => {
            if (!sendFullContext) {
                return { entity: entityDescriptor };
            }

            let nextMatchedKeys = matchedKeys;
            let contextObject: unknown;

            if (resolveContextData) {
                const resolved = normalizeResolveResult(
                    resolveContextData(userMessage, attachedItems, matchedKeys),
                );
                if (resolved.matchedKeys) {
                    nextMatchedKeys = resolved.matchedKeys;
                    onMatchedKeysChange(resolved.matchedKeys);
                }
                contextObject = resolved.context;
            } else if (contextData !== undefined) {
                contextObject = contextData;
            }

            let file: AiContext['file'];
            const fileAttachment = attachedItems.find(item => isAiFileAttachGroupKey(item.type));
            if (fileAttachment && isPlainDocument(fileAttachment.data)) {
                const documentFile = await loadDocumentAsAiFile(
                    fileAttachment.data,
                    resolveDocumentApiSide(utilityKey),
                );
                file = {
                    name: documentFile.name,
                    type: documentFile.type,
                    size: documentFile.size,
                    content: documentFile.content,
                };
            }

            const dataJson =
                contextObject !== undefined ? stringifyContextForAi(contextObject) : undefined;
            if (dataJson && dataJson.length > AI_MAX_DATA_JSON_CHARS) {
                throw new Error(
                    `Context is too large for AI (${dataJson.length.toLocaleString()} chars; max ${AI_MAX_DATA_JSON_CHARS.toLocaleString()}). Remove some attachments and try again.`,
                );
            }

            // Keep fingerprint in sync with keys used for this payload.
            lastSentContextFingerprintRef.current = buildContextFingerprint(attachedItems, nextMatchedKeys);

            return {
                entity: entityDescriptor,
                dataJson,
                file,
            };
        };

        const handleSend = async () => {
            const trimmed = question.trim();
            if (!trimmed || chatLoading || historyLoading || !historyReady) {
                return;
            }

            setInternalError(null);
            const isSuggestReply = selectedPresetId === 'suggestReply';

            const userMessage = trimmed;
            setMessages(prev => [...prev, { id: generateLocalId(), role: 'user', text: userMessage }]);
            setQuestion('');

            try {
                let utility = buildUtilityFromKey(utilityKey);

                if (isSuggestReply) {
                    utility += `\n\nFormatting requirements: For EACH suggested reply, wrap it inside markers:\n<<<REPLY_START>>>\nyour reply here\n<<<REPLY_END>>>\n\nAny text outside markers will be treated as regular text and will be displayed by the frontend in the same order (text, reply box, text, reply box). Copy action must include only marker reply contents.`;
                }

                const fingerprint = buildContextFingerprint(attachedItems, matchedKeys);
                const sendFullContext =
                    lastSentContextFingerprintRef.current === null ||
                    lastSentContextFingerprintRef.current !== fingerprint;

                const context = await buildContextPayload(userMessage, sendFullContext);

                const result = await askAi(conversationId, action, trimmed, context, utility);
                const assistantText = result.assistantMessage.content;
                setMessages(prev => [
                    ...prev,
                    {
                        id: result.assistantMessage.id || generateLocalId(),
                        role: 'assistant',
                        text: assistantText,
                    },
                ]);
                // After an answer → jump to the top of the new reply.
                pendingScrollRef.current = 'top-of-last';
            } catch (e: any) {
                setInternalError(typeof e === 'string' ? e : e?.message || 'AI error');
            }
        };

        const handleCancel = () => {
            setQuestion('');
            setInternalError(null);
            setShowHowToGif(false);
            onClose();
        };

        const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = event => {
            if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                event.preventDefault();
                void handleSend();
            }
        };

        const handleToggleAttachment = (groupKey: string, item: AttachableEntityItem) => {
            if (item.disabled) {
                return;
            }

            const exists = attachedItems.some(existing => existing.type === groupKey && existing.id === item.id);
            if (exists) {
                onAttachedItemsChange(
                    attachedItems.filter(existing => !(existing.type === groupKey && existing.id === item.id)),
                );
                return;
            }

            // Only one file-bearing item can be attached (sent via context.file).
            const withoutConflictingFiles = isAiFileAttachGroupKey(groupKey)
                ? attachedItems.filter(existing => !isAiFileAttachGroupKey(existing.type))
                : attachedItems;

            onAttachedItemsChange([
                ...withoutConflictingFiles,
                {
                    type: groupKey,
                    id: item.id,
                    label: `${groupKey}: ${item.summary}`,
                    data: item.data,
                },
            ]);
        };

        const handleToggleAllAttachments = (
            groupKey: string,
            items: AttachableEntityItem[],
            selectAll: boolean,
        ) => {
            const selectable = items.filter(item => !item.disabled);

            if (!selectAll) {
                const ids = new Set(selectable.map(item => item.id));
                onAttachedItemsChange(
                    attachedItems.filter(existing => !(existing.type === groupKey && ids.has(existing.id))),
                );
                return;
            }

            // File attaches remain single-select even via Select all.
            if (isAiFileAttachGroupKey(groupKey)) {
                const first = selectable[0];
                if (!first) {
                    return;
                }
                handleToggleAttachment(groupKey, first);
                return;
            }

            const existingIds = new Set(
                attachedItems.filter(item => item.type === groupKey).map(item => item.id),
            );
            const toAdd = selectable
                .filter(item => !existingIds.has(item.id))
                .map(item => ({
                    type: groupKey,
                    id: item.id,
                    label: `${groupKey}: ${item.summary}`,
                    data: item.data,
                }));

            if (!toAdd.length) {
                return;
            }

            onAttachedItemsChange([...attachedItems, ...toAdd]);
        };

        const handleRemoveAttachment = (type: string, id: string) => {
            onAttachedItemsChange(attachedItems.filter(item => !(item.type === type && item.id === id)));
        };

        useLayoutEffect(() => {
            const intent = pendingScrollRef.current;
            if (!intent || !historyReady) {
                return;
            }
            pendingScrollRef.current = null;

            // Wait a frame so message DOM (markdown) is laid out.
            const raf = window.requestAnimationFrame(() => {
                if (intent === 'bottom') {
                    messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
                    return;
                }
                lastMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
            return () => window.cancelAnimationFrame(raf);
        }, [historyReady, messages]);

        const hasAssistantReply = messages.some(m => m.role === 'assistant');
        const showAttach = !!attachableEntities?.length;
        const lastMessageId = messages.length > 0 ? messages[messages.length - 1].id : null;

        const renderAssistantMessage = (message: UiChatMessage, isLastMessage: boolean): React.ReactNode => {
            if (hasSuggestReplyMarkers(message.text)) {
                const segments = parseSuggestReplyResponseToSegments(message.text);
                return (
                    <React.Fragment key={message.id}>
                        {segments.map((seg, idx) =>
                            seg.type === 'text' ? (
                                <S.MessageRow
                                    key={`${message.id}-text-${idx}`}
                                    role="assistant"
                                    ref={isLastMessage && idx === 0 ? lastMessageRef : undefined}>
                                    <S.MessageBubble role="assistant">
                                        <S.MarkdownBlock text={seg.text} />
                                    </S.MessageBubble>
                                </S.MessageRow>
                            ) : (
                                <S.SuggestedReplyContainer
                                    key={`${message.id}-reply-${idx}`}
                                    ref={isLastMessage && idx === 0 ? lastMessageRef : undefined}>
                                    <S.MarkdownBlock text={seg.text} />
                                    <div style={{ marginTop: 4, textAlign: 'right' }}>
                                        <CopyAnswerButton text={seg.text} />
                                    </div>
                                </S.SuggestedReplyContainer>
                            ),
                        )}
                    </React.Fragment>
                );
            }

            return (
                <S.MessageRow
                    key={message.id}
                    role="assistant"
                    ref={isLastMessage ? lastMessageRef : undefined}>
                    <S.MessageBubble role="assistant">
                        <S.MarkdownBlock text={message.text} />
                    </S.MessageBubble>
                    {message.text.trim() && (
                        <div style={{ marginTop: 4, textAlign: 'right' }}>
                            <CopyAnswerButton text={message.text} />
                        </div>
                    )}
                </S.MessageRow>
            );
        };

        const content = (
            <S.FloatingContainer
                isDragging={dragging}
                style={{
                    left: `${position.x}px`,
                    top: `${position.y}px`,
                }}>
                <S.Header onMouseDown={handleMouseDownHeader}>
                    <S.HeaderGroup>
                        <S.Title>Talk to AI</S.Title>
                        <Tooltip direction="top" variant="dark" title={<span>How to use this?</span>}>
                            <div
                                onMouseDown={e => e.stopPropagation()}
                                onClick={e => {
                                    e.stopPropagation();
                                }}>
                                <S.IconButton variant="text" type="button" onClick={() => setShowHowToGif(v => !v)}>
                                    <Icon path={mdiInformationOutline} size={0.75} />
                                </S.IconButton>
                            </div>
                        </Tooltip>
                    </S.HeaderGroup>
                    <S.HeaderActions>
                        <S.CloseButton variant="text" onClick={handleCancel}>
                            <Icon path={mdiClose} size={0.8} />
                        </S.CloseButton>
                    </S.HeaderActions>
                </S.Header>
                {showHowToGif && (
                    <S.HelpGifContainer onMouseDown={e => e.stopPropagation()}>
                        <S.HelpGifTopRow>
                            <S.Title>How to use this?</S.Title>
                            <S.HelpGifCloseButton
                                variant="text"
                                type="button"
                                onClick={e => {
                                    e.stopPropagation();
                                    setShowHowToGif(false);
                                }}>
                                <Icon path={mdiClose} size={0.8} />
                            </S.HelpGifCloseButton>
                        </S.HelpGifTopRow>
                        <S.HelpGifImage src={talkToClaimGif} alt="Talk to AI instructions" />
                    </S.HelpGifContainer>
                )}
                {(messages.length > 0 || historyLoading || chatLoading || error || internalError) && (
                    <S.Body>
                        <S.MessagesContainer ref={messagesContainerRef}>
                            {historyLoading && !messages.length && (
                                <S.MessageRow role="assistant">
                                    <S.MessageBubble role="assistant">Loading conversation…</S.MessageBubble>
                                </S.MessageRow>
                            )}
                            {messages.map(message =>
                                message.role === 'assistant' ? (
                                    renderAssistantMessage(message, message.id === lastMessageId)
                                ) : (
                                    <S.MessageRow
                                        key={message.id}
                                        role={message.role}
                                        ref={message.id === lastMessageId ? lastMessageRef : undefined}>
                                        <S.MessageBubble role={message.role}>{message.text}</S.MessageBubble>
                                    </S.MessageRow>
                                ),
                            )}
                            {chatLoading && (
                                <S.MessageRow role="assistant">
                                    <S.MessageBubble role="assistant">
                                        Thinking…
                                        <div>This may take up to 5 minutes. Please don’t close this window.</div>
                                    </S.MessageBubble>
                                </S.MessageRow>
                            )}
                            {(error || internalError) && <S.ErrorText>{internalError || error}</S.ErrorText>}

                            <div ref={messagesEndRef} />
                        </S.MessagesContainer>
                    </S.Body>
                )}
                <S.Footer>
                        {presets.length > 0 && !hasAssistantReply && (
                            <S.PresetButtonsRow>
                                {presets.map(preset => (
                                    <S.SecondaryButton
                                        key={preset.id}
                                        variant="outlined"
                                        type="button"
                                        disabled={chatLoading || historyLoading || !historyReady}
                                        onClick={() => {
                                            setAction(preset.action);
                                            setSelectedPresetId(preset.id);
                                            setQuestion(
                                                preset.template.replace(
                                                    /\{\{\s*subject\s*\}\}/gi,
                                                    subjectLabel || 'item',
                                                ),
                                            );
                                        }}>
                                        {preset.label}
                                    </S.SecondaryButton>
                                ))}
                            </S.PresetButtonsRow>
                        )}
                        {attachedItems.length > 0 && (
                            <S.AttachmentChipsRow>
                                {attachedItems.map(item => (
                                    <S.AttachmentChip key={`${item.type}:${item.id}`}>
                                        <S.AttachmentChipText title={item.label}>{item.label}</S.AttachmentChipText>
                                        <S.AttachmentChipRemove
                                            type="button"
                                            aria-label={`Remove ${item.label}`}
                                            onClick={() => handleRemoveAttachment(item.type, item.id)}>
                                            <Icon path={mdiClose} size={0.5} />
                                        </S.AttachmentChipRemove>
                                    </S.AttachmentChip>
                                ))}
                            </S.AttachmentChipsRow>
                        )}
                        <S.InputWrapper>
                            <S.InputContainer>
                                <S.InputBox>
                                    <S.Textarea
                                        value={question}
                                        onChange={event => setQuestion(event.target.value)}
                                        onKeyDown={handleKeyDown}
                                        placeholder="Type your question for AI..."
                                        disabled={chatLoading || historyLoading || !historyReady}
                                        disableLatin1Validation
                                        rows={2}
                                    />
                                </S.InputBox>
                                <S.ActionsWrapper>
                                    {showAttach && (
                                        <AttachCascadingSelect
                                            groups={attachableEntities!}
                                            attachedIdsByType={attachedIdsByType}
                                            disabled={chatLoading || historyLoading || !historyReady}
                                            onToggleItem={handleToggleAttachment}
                                            onToggleAll={handleToggleAllAttachments}
                                        />
                                    )}
                                    <S.IconButton
                                        variant="primary"
                                        type="button"
                                        onClick={handleSend}
                                        disabled={
                                            chatLoading ||
                                            historyLoading ||
                                            !historyReady ||
                                            !question.trim()
                                        }>
                                        <Icon path={mdiSend} size={0.7} />
                                    </S.IconButton>
                                </S.ActionsWrapper>
                            </S.InputContainer>
                        </S.InputWrapper>
                    </S.Footer>
            </S.FloatingContainer>
        );

        return ReactDOM.createPortal(content, document.body);
    },
);
